import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { CompactPagination } from 'src/components/organisms/compact-pagination';
import { useAssignSpaceGroupsToEmployeeGroup } from 'src/features/employees-groups/api/hooks/space-groups/use-assign-space-groups-to-employee-group';
import { fetchSpaceGroups } from 'src/features/space-groups/api/lib/space-groups';
import { spaceGroupsKeys } from 'src/features/space-groups/api/query-keys/space-groups';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { usePagination } from 'src/hooks/use-pagination';
import { useSearchState } from 'src/hooks/use-search-state';
import { useUserStore } from 'src/store/user-store';

interface Props extends DialogActions {
  employeeSpaceGroupsTokens: string[];
}

export const AssignSpaceGroupsToEmployeeGroupDialog = ({
  open,
  onCancel,
  employeeSpaceGroupsTokens,
}: Props) => {
  const { t } = useTranslation(['employeesGroups']);

  const { employeeGroupToken } = useParams({
    from: '/_layout/tenant/employees/groups/$groupToken/_layout/space-groups',
    select(state) {
      return {
        employeeGroupToken: state.groupToken,
      };
    },
  });

  const { directoryToken } = useUserStore();

  const [selectedSpaceGroups, setSelectedSpaceGroups] = useState<string[]>([]);

  const [searchState, setSearchState] = useSearchState();

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const { results, isLoading, isFetching, error } = useMeilisearch({
    fetch: fetchSpaceGroups,
    page,
    perPage,
    queryKey: spaceGroupsKeys.list(
      directoryToken ?? '',
      page,
      perPage,
      searchState,
    ),
    searchState,
    enabled: open,
  });

  useEffect(() => {
    if (!results) return;
    setTotalItems(results.estimatedTotalHits);
  }, [results]);

  const handleCancel = () => {
    onCancel?.();
    setSelectedSpaceGroups([]);
  };

  const {
    mutate: assignSpaceGroupsToEmployeeGroup,
    isPending: isAssigningSpaceGroupsToEmployeeGroup,
  } = useAssignSpaceGroupsToEmployeeGroup(handleCancel);

  const handleChangeQuery = (query: string) => {
    setSearchState(prevState => ({
      ...prevState,
      q: query,
    }));
  };

  const handleUpdateSelectedSpaceGroups = (employeeGroupToken: string) => {
    setSelectedSpaceGroups(prevState => {
      if (prevState.includes(employeeGroupToken)) {
        return prevState.filter(token => token !== employeeGroupToken);
      }
      return [...prevState, employeeGroupToken];
    });
  };

  const handleAssignSpaceGroupsToEmployeeGroup = () => {
    assignSpaceGroupsToEmployeeGroup({
      token: employeeGroupToken,
      spaceGroupTokens: selectedSpaceGroups,
    });
  };

  const isSpaceGroupAlreadyAssignedToEmployeeGroup = (
    spaceGroupToken: string,
  ) => {
    return employeeSpaceGroupsTokens.includes(spaceGroupToken);
  };

  return (
    <Dialog
      open={open}
      title={t(
        'employeesGroups:spaceGroups.assignSpaceGroupToEmployeeGroupDialog.title',
      )}
      onCancel={handleCancel}
      onConfirm={handleAssignSpaceGroupsToEmployeeGroup}
      isConfirmButtonSubmitting={isAssigningSpaceGroupsToEmployeeGroup}
      isConfirmButtonDisabled={selectedSpaceGroups.length === 0}
      confirmButtonText={t(
        'employeesGroups:spaceGroups.assignSpaceGroupToEmployeeGroupDialog.confirmButtonText',
      )}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="body2">
          {t(
            'employeesGroups:spaceGroups.assignSpaceGroupToEmployeeGroupDialog.description',
          )}
        </Typography>

        <Query
          searchAsType
          disabled={false}
          onChange={handleChangeQuery}
          sx={{
            minWidth: 200,
          }}
          value={searchState.q}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          {results?.hits?.map(spaceGroup => (
            <Tooltip
              key={spaceGroup.token}
              title={t(
                'employeesGroups:spaceGroups.assignSpaceGroupToEmployeeGroupDialog.alreadyAssigned',
              )}
              placement="right"
              disableFocusListener={
                !isSpaceGroupAlreadyAssignedToEmployeeGroup(spaceGroup.token)
              }
              disableHoverListener={
                !isSpaceGroupAlreadyAssignedToEmployeeGroup(spaceGroup.token)
              }>
              <span>
                <CardWithContent
                  isSelected={selectedSpaceGroups.includes(spaceGroup.token)}
                  invalid={isSpaceGroupAlreadyAssignedToEmployeeGroup(
                    spaceGroup.token,
                  )}
                  onClick={() => {
                    handleUpdateSelectedSpaceGroups(spaceGroup.token);
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={
                        selectedSpaceGroups.includes(spaceGroup.token) ||
                        isSpaceGroupAlreadyAssignedToEmployeeGroup(
                          spaceGroup.token,
                        )
                      }
                      disabled={isSpaceGroupAlreadyAssignedToEmployeeGroup(
                        spaceGroup.token,
                      )}
                    />
                    <Box>
                      <Typography variant="body2">{spaceGroup.name}</Typography>
                    </Box>
                  </Box>
                </CardWithContent>
              </span>
            </Tooltip>
          ))}

          <DataStatusIndicator
            dataLength={results?.hits?.length ?? 0}
            isFetching={isLoading}
            error={error?.response?.data?.message}
          />

          <CompactPagination
            disabled={isLoading}
            isFetching={isFetching}
            page={page}
            changePage={changePage}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
            perPage={perPage}
            totalPages={totalPages}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
